import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { IconButton, Snackbar, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CopyIcon from "@mui/icons-material/FileCopy";
import { v4 as uuidv4 } from "uuid";
import { useAtom } from "jotai";
import { themeAtom, isAdminAtom } from "../../../../-Atoms";
import { db, doc, setDoc } from "../../../../firebaseConfig";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import { motion } from "framer-motion";

const ContactsV2 = ({ client }) => {
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);

  const [contacts, setContacts] = useState(
    Array.isArray(client?.contacts)
      ? client.contacts.map((contact) => ({
          ...contact,
          id: contact.id || uuidv4(),
        }))
      : []
  );
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    setContacts(
      Array.isArray(client?.contacts)
        ? client.contacts.map((contact) => ({
            ...contact,
            id: contact.id || uuidv4(),
          }))
        : []
    );
  }, [client]);

  const updateFS = debounce(async (id, field, value) => {
    setDoc(doc(db, "ClientDatabase", id), { [field]: value }, { merge: true });
  }, 100);

  const handleAddContact = () => {
    if (!isAdmin) return;
    const newContact = {
      id: uuidv4(),
      name: "",
      title: "",
      email: "",
      phone: "",
      isSupply: false,
      isBilling: false,
    };
    const updatedContacts = [...contacts, newContact];
    setContacts(updatedContacts);
    updateFS(client.id, "contacts", updatedContacts);
  };

  const handleDeleteContacts = () => {
    if (!isAdmin || rowSelectionModel.length === 0) return;
    const updatedContacts = contacts.filter(
      (contact) => !rowSelectionModel.includes(contact.id)
    );
    setContacts(updatedContacts);
    updateFS(client.id, "contacts", updatedContacts);
    setRowSelectionModel([]);
  };

  const handleCopyEmails = () => {
    const selectedContacts = contacts.filter((contact) =>
      rowSelectionModel.includes(contact.id)
    );
    const emailsToCopy = selectedContacts.map((contact) => contact.email);
    const emailCount = emailsToCopy.length;

    if (emailCount > 0) {
      navigator.clipboard.writeText(emailsToCopy.join(", ")).then(() => {
        setSnackbarMessage(`${emailCount} email(s) copied to clipboard`);
        setSnackbarOpen(true);
      });
    }
  };

  const handleClipboardCopy = () => {
    setSnackbarMessage("Email copied to clipboard");
    setSnackbarOpen(true);
  };

  const columns = [
    { field: "name", headerName: "Name", width: 150, editable: isAdmin },
    { field: "title", headerName: "Title", width: 120, editable: isAdmin },
    { field: "email", headerName: "Email", width: 210, editable: isAdmin },
    {
      field: "phone",
      headerName: "Phone",
      width: 120,
      editable: isAdmin,
    },
    {
      field: "isSupply",
      headerName: "Supply",
      type: "boolean",
      width: 70,
      editable: isAdmin,
    },
    {
      field: "isBilling",
      headerName: "Billing",
      type: "boolean",
      width: 60,
      editable: isAdmin,
    },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
    hover: { scale: 1.02 },
    tap: { scale: 0.98 },
  };

  return (
    <FormControl
      fullWidth
      sx={{
        position: "relative",
      }}
      variant="filled"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 1.5,
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color:
                  theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                fontWeight: "bold",
                fontSize: "1.1rem",
              }}
            >
              Contacts
            </Typography>
            <Box sx={{ display: isAdmin ? "flex" : "none" }}>
              <motion.div
                variants={itemVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <IconButton
                  onClick={handleCopyEmails}
                  disabled={rowSelectionModel.length === 0}
                  sx={{
                    backgroundColor: theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(245,245,245,0.9)",
                    '&:hover': {
                      backgroundColor: theme === "dark" ? "rgba(55,55,55,0.9)" : "rgba(235,235,235,1)",
                    }
                  }}
                >
                  <CopyIcon />
                </IconButton>
              </motion.div>
              <motion.div
                variants={itemVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <IconButton
                  onClick={handleAddContact}
                  sx={{
                    ml: 1,
                    backgroundColor: theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(245,245,245,0.9)",
                    '&:hover': {
                      backgroundColor: theme === "dark" ? "rgba(55,55,55,0.9)" : "rgba(235,235,235,1)",
                    }
                  }}
                >
                  <AddIcon />
                </IconButton>
              </motion.div>
              <motion.div
                variants={itemVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <IconButton
                  onClick={handleDeleteContacts}
                  disabled={rowSelectionModel.length === 0}
                  sx={{
                    ml: 1,
                    backgroundColor: theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(245,245,245,0.9)",
                    '&:hover': {
                      backgroundColor: theme === "dark" ? "rgba(55,55,55,0.9)" : "rgba(235,235,235,1)",
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </motion.div>
            </Box>
          </Box>
          <Box
            sx={{
              '& .MuiDataGrid-root': {
                border: 'none',
                backgroundColor: theme === "dark" ? "rgba(45,45,45,0.5)" : "rgba(250,250,250,0.5)",
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: theme === "dark" ? "0 2px 5px rgba(0,0,0,0.2)" : "0 2px 5px rgba(0,0,0,0.05)",
              },
              '& .MuiDataGrid-cell': {
                borderBottom: theme === "dark" ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: theme === "dark" ? "rgba(40,40,40,0.9)" : "rgba(240,240,240,0.9)",
                borderBottom: 'none',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold',
              },
            }}
          >
            <DataGrid
              rows={contacts}
              columns={columns}
              columnVisibilityModel={{
                isBilling: isAdmin,
              }}
              disableColumnSorting
              cellSelection
              checkboxSelection={isAdmin}
              disableRowSelectionOnClick
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={(newSelection) => {
                setRowSelectionModel(newSelection);
              }}
              processRowUpdate={(updatedRow) => {
                const updatedContacts = contacts.map((contact) =>
                  contact.id === updatedRow.id ? updatedRow : contact
                );
                setContacts(updatedContacts);
                updateFS(client.id, "contacts", updatedContacts);
                return updatedRow;
              }}
              onClipboardCopy={handleClipboardCopy}
              onProcessRowUpdateError={(error) => console.error(error)}
              getRowId={(row) => row.id}
              autoHeight
              autosizeOnMount
              autosizeOptions={{
                columns: ["name"],
                includeOutliers: true,
                includeHeaders: true,
              }}
              density={"compact"}
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
              }}
            />
          </Box>
        </motion.div>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </FormControl>
  );
};

export default ContactsV2;

function debounce(func, wait, immediate) {
  var timeout;
  return function () {
    var context = this,
      args = arguments;
    var later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    var callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}
